// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * Orbit maths that needs no propagation: the size and shape of an orbit
 * straight from its mean elements, the orbit class AYNI groups by, and the
 * colours each grouping draws with.
 *
 * The colours were picked to stand out on the GIBS true-colour Earth (dark
 * blue sea, green and tan land, white cloud and ice): saturated, light
 * hues that none of those share. Every dot and trail also gets a dark rim
 * where it is drawn on the globe, so it still reads over white cloud.
 */
import type { SatelliteOmm } from './satelliteTypes'

/** Earth's gravitational parameter, km³/s² (WGS-72, as SGP4 uses). */
export const MU_KM3_S2 = 398600.8
/** Earth's equatorial radius, km (WGS-72). */
export const EARTH_RADIUS_KM = 6378.135
/** Mean Earth radius the globes are drawn with, km. */
export const GLOBE_RADIUS_KM = 6371

export type OrbitClass = 'leo' | 'meo' | 'geo' | 'heo' | 'high'

export const ORBIT_CLASSES: readonly OrbitClass[] = ['leo', 'meo', 'geo', 'heo', 'high']

export interface OrbitShape {
  /** Minutes per revolution. */
  periodMin: number
  /** Semi-major axis, km from Earth's centre. */
  semiMajorKm: number
  /** Lowest height above the surface, km. */
  perigeeKm: number
  /** Highest height above the surface, km. */
  apogeeKm: number
  inclinationDeg: number
  eccentricity: number
}

export function orbitShape(omm: Pick<SatelliteOmm, 'MEAN_MOTION' | 'ECCENTRICITY' | 'INCLINATION'>): OrbitShape {
  const revPerDay = omm.MEAN_MOTION
  const n = (revPerDay * 2 * Math.PI) / 86400
  const a = Math.cbrt(MU_KM3_S2 / (n * n))
  const e = omm.ECCENTRICITY
  return {
    periodMin: 1440 / revPerDay,
    semiMajorKm: a,
    perigeeKm: a * (1 - e) - EARTH_RADIUS_KM,
    apogeeKm: a * (1 + e) - EARTH_RADIUS_KM,
    inclinationDeg: omm.INCLINATION,
    eccentricity: e,
  }
}

/**
 * The orbit class:
 * - LEO: never higher than 2,000 km.
 * - GEO: geosynchronous, one turn a sidereal day, nearly round
 *   (the geostationary belt and its inclined and graveyard neighbours).
 * - HEO: strongly elliptical (Molniya, Tundra, transfer orbits).
 * - MEO: round orbits between LEO and GEO (GPS, Galileo, GLONASS, BeiDou).
 * - high: round orbits beyond GEO.
 */
export function orbitClass(shape: OrbitShape): OrbitClass {
  if (shape.apogeeKm < 2000) return 'leo'
  const revPerDay = 1440 / shape.periodMin
  if (revPerDay > 0.9 && revPerDay < 1.1 && shape.eccentricity < 0.1) return 'geo'
  if (shape.eccentricity >= 0.25) return 'heo'
  if (revPerDay >= 1.1) return 'meo'
  return 'high'
}

/** Colour per orbit class (sRGB hex). */
export const ORBIT_COLOURS: Record<OrbitClass, string> = {
  leo: '#3fe6ff',
  meo: '#b8ff3d',
  geo: '#ffb020',
  heo: '#ff4fd8',
  high: '#c9a7ff',
}

/** Purposes, as the satellite catalogue file names them. */
export type SatellitePurpose =
  | 'human-spaceflight'
  | 'communications'
  | 'earth-observation'
  | 'weather'
  | 'navigation'
  | 'science'
  | 'military'
  | 'technology'
  | 'debris'
  | 'other'

export const PURPOSES: readonly SatellitePurpose[] = [
  'human-spaceflight',
  'communications',
  'earth-observation',
  'weather',
  'navigation',
  'science',
  'military',
  'technology',
  'debris',
  'other',
]

export const PURPOSE_COLOURS: Record<SatellitePurpose, string> = {
  'human-spaceflight': '#ffffff',
  communications: '#3fe6ff',
  'earth-observation': '#b8ff3d',
  weather: '#ffe14d',
  navigation: '#ffb020',
  science: '#c9a7ff',
  military: '#ff5a5a',
  technology: '#ff9be6',
  debris: '#a7b0ba',
  other: '#d8d8d8',
}

/**
 * Owner groups for colouring by country. SATCAT OWNER codes; everything
 * else is "other". The order is the legend's.
 */
export const COUNTRY_GROUPS = ['US', 'PRC', 'CIS', 'UK', 'ESA', 'JPN', 'IND', 'FR', 'other'] as const
export type CountryGroup = (typeof COUNTRY_GROUPS)[number]

export const COUNTRY_COLOURS: Record<CountryGroup, string> = {
  US: '#3fe6ff',
  PRC: '#ff5a5a',
  CIS: '#ffb020',
  UK: '#b8ff3d',
  ESA: '#5b8cff',
  JPN: '#ff9be6',
  IND: '#ff8a3d',
  FR: '#c9a7ff',
  other: '#d8d8d8',
}

/** Several SATCAT owner codes belong to one group. */
const COUNTRY_ALIASES: Record<string, CountryGroup> = {
  US: 'US',
  PRC: 'PRC',
  CIS: 'CIS',
  RUS: 'CIS',
  UK: 'UK',
  ESA: 'ESA',
  EUME: 'ESA',
  EUTE: 'ESA',
  JPN: 'JPN',
  IND: 'IND',
  FR: 'FR',
}

export function countryGroup(owner: string | undefined): CountryGroup {
  if (!owner) return 'other'
  return COUNTRY_ALIASES[owner.toUpperCase()] ?? 'other'
}

/** Debris colour when debris is shown while grouping by orbit or country. */
export const DEBRIS_COLOUR = '#a7b0ba'

/** '#rrggbb' → [r, g, b] in 0..1. */
export function hexToRgb01(hex: string): [number, number, number] {
  const v = parseInt(hex.slice(1), 16)
  return [((v >> 16) & 255) / 255, ((v >> 8) & 255) / 255, (v & 255) / 255]
}
